import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useAppDispatch } from '../../../../../../configuration/setup/hooks';
import { useUpdateAccountMutation } from '../../../../../services/accountsApi';
import { type Account, LifeCycleState } from '../../../../../services/schemas/accountsBackendCodec';
import { ZalandoErrorNotificationContent } from '../../../../common/ZalandoErrorNotificationContent';
import { type FormAccount, toAccount, toFormAccount } from '../../formAccount';
import { resetAccountsSidebar } from '../../sidebar/accountSidebarSlice';
import { EditAccountConfirmation } from './EditAccountConfirmation';
import { EditAccountForm } from './EditAccountForm';

const editAccountStep = {
    EDIT_ACCOUNT: 'EDIT_ACCOUNT',
    CONFIRM_EDIT_ACCOUNT: 'CONFIRM_EDIT_ACCOUNT',
} as const;

type EditAccountStep = (typeof editAccountStep)[keyof typeof editAccountStep];

const accountIsLocked = (account: Account) => account.lifeCycleState === LifeCycleState.locked;
const accountIsActive = (account: Account) => account.lifeCycleState === LifeCycleState.active;
const accountIsInUnexpectedState = (account: Account) => !accountIsLocked(account) && !accountIsActive(account);

export const EditAccountModalContent = ({ accountInSidebar }: { accountInSidebar: Account }) => {
    const formAccountInSidebar = toFormAccount(accountInSidebar);
    const [formAccountToUpdate, setFormAccountToUpdate] = useState(toFormAccount(accountInSidebar));
    const [step, setStep] = useState<EditAccountStep>(editAccountStep.EDIT_ACCOUNT);

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [
        updateAccountRtk,
        { isLoading: updateIsLoading, isSuccess: updateIsSuccess, isError: updateIsError, error: updateError },
    ] = useUpdateAccountMutation();

    const form = useForm<FormAccount>({
        defaultValues: toFormAccount(accountInSidebar),
        mode: 'all',
    });

    // Trigger validation at initial loading of the form
    // This is reasonable for UX as we only use the form for updating and the initial state should be valid.
    // If the initial state is not valid we should show the user directly which fields prevent them from saving.
    useEffect(() => {
        const initializeForm = async () => {
            await form.trigger();
        };
        initializeForm().catch(console.error);
    }, [form.trigger]);

    useEffect(() => {
        if (formAccountInSidebar.company.id !== formAccountToUpdate.company.id) {
            setFormAccountToUpdate(formAccountInSidebar);
            form.reset(accountInSidebar);
        }
    }, [accountInSidebar, form.reset, formAccountToUpdate.company.id]);

    useEffect(() => {
        if (updateIsSuccess) {
            Notification.success(intl.formatMessage({ id: 'intl-msg:helpgang.notification.accountUpdateSuccess' }));
            dispatch(resetAccountsSidebar());
        }
    }, [updateIsSuccess, intl, dispatch]);

    useEffect(() => {
        if (updateIsError) {
            Notification.error(<ZalandoErrorNotificationContent rtkError={updateError} />, {
                title: intl.formatMessage({ id: 'intl-msg:helpgang.notification.couldNotUpdateAccount' }),
            });
        }
    }, [updateIsError, updateError, intl]);

    const handleUpdateConfirm = (account: Account) => (reason: string) => {
        updateAccountRtk({ account, reason });
    };

    const onSubmit = (editedAccountData: FormAccount) => {
        setFormAccountToUpdate(editedAccountData);
        setStep(editAccountStep.CONFIRM_EDIT_ACCOUNT);
    };

    return step === editAccountStep.EDIT_ACCOUNT ? (
        <div className={'edit-account-content'}>
            <FormProvider {...form}>
                <EditAccountForm
                    onSubmit={onSubmit}
                    accountIsInUnexpectedState={accountIsInUnexpectedState(accountInSidebar)}
                    accountType={formAccountToUpdate.company.accountType}
                />
            </FormProvider>
        </div>
    ) : (
        <div className={'confirm-edit-account-content'}>
            <EditAccountConfirmation
                formAccount={formAccountToUpdate}
                oldFormAccount={formAccountInSidebar}
                dirtyFields={form.formState.dirtyFields}
                updateDialogIsLoading={updateIsLoading}
                handleUpdateConfirm={handleUpdateConfirm(toAccount(formAccountToUpdate))}
                cancelUpdate={() => setStep(editAccountStep.EDIT_ACCOUNT)}
            />
        </div>
    );
};
